/**
 * ToolCallCard — §4.5 Expanded Trust Visualization
 *
 * The PRIMARY TRUST MECHANISM for enterprise AI responses.
 * Every tool call renders as an inline card showing:
 *   - Tool name + status icon
 *   - Input parameters
 *   - Raw output (syntax highlighted, copyable)
 *   - Latency + policy badge + source attribution
 *
 * Expanded by default on first appearance. Collapsible on subsequent views.
 */

import { useState } from 'react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { Badge } from '../ui/badge';
import {
  Wrench,
  Check,
  X,
  Clock,
  Lock,
  ChevronRight,
  Copy,
  CheckCheck,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ApprovalCard } from './approval-card';

interface ToolCallCardProps {
  toolName: string;
  args: Record<string, unknown>;
  result: unknown;
  durationMs: number;
  status: 'success' | 'failed' | 'pending' | 'approval_required';
  policyAction: 'auto_approve' | 'require_approval' | 'forbidden';
  error?: string;
  /** First time showing this card in the response */
  isFirstAppearance?: boolean;
  /**
   * Wave 2D — UUID of the agent_tool_approvals row.
   * When present and policyAction is require_approval, renders ApprovalCard.
   */
  approvalId?: string;
  approvalSummary?: string;
}

export function ToolCallCard({
  toolName,
  args,
  result,
  durationMs,
  status,
  policyAction,
  error,
  isFirstAppearance = true,
  approvalId,
  approvalSummary,
}: ToolCallCardProps) {
  const [isOpen, setIsOpen] = useState(isFirstAppearance);
  const [copied, setCopied] = useState(false);

  const statusIcon = STATUS_ICONS[status];
  const statusColor = STATUS_COLORS[status];
  const policyBadge = POLICY_BADGES[policyAction];

  const outputStr = result != null
    ? JSON.stringify(result, null, 2)
    : error ?? 'No output';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(outputStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="my-3">
      <div className={cn(
        'rounded-lg border bg-card text-card-foreground',
        status === 'failed' && 'border-destructive/30',
        status === 'approval_required' && 'border-yellow-500/30',
      )}>
        {/* Header — always visible */}
        <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            <span className="font-mono text-sm font-medium">{toolName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('flex items-center gap-1 text-xs', statusColor)}>
              {statusIcon}
              {STATUS_LABELS[status]}
            </span>
            <ChevronRight className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              isOpen && 'rotate-90'
            )} />
          </div>
        </CollapsibleTrigger>

        {/* Body — collapsible */}
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3 border-t">
            {/* Input */}
            <div className="mt-3">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Input</span>
              <pre className="mt-1 p-2 rounded bg-muted text-xs font-mono overflow-x-auto max-h-32">
                {JSON.stringify(args, null, 2)}
              </pre>
            </div>

            {/* Output */}
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Output</span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copied ? <CheckCheck className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre className={cn(
                'mt-1 p-2 rounded text-xs font-mono overflow-x-auto max-h-48',
                status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-muted'
              )}>
                {outputStr}
              </pre>
            </div>

            {/* Footer: latency + policy + source */}
            <div className="flex items-center gap-3 pt-2 border-t text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {durationMs}ms
              </span>
              <Badge variant={policyBadge.variant as any} className="text-xs">
                {policyBadge.icon} {policyBadge.label}
              </Badge>
              <span>Source: {getToolSource(toolName)}</span>
            </div>

            {/* Wave 2D — inline approval UI */}
            {policyAction === 'require_approval' &&
              (status === 'approval_required' || status === 'pending') && (
                <ApprovalCard
                  approvalId={approvalId}
                  args={args}
                  summary={approvalSummary}
                />
              )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ── Confidence Badge Component ──────────────────────────────────────────────

interface ConfidenceBadgeProps {
  score: number;
  onClick?: () => void;
}

export function ConfidenceBadge({ score, onClick }: ConfidenceBadgeProps) {
  const level = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';

  const styles = {
    high: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    low: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };

  const labels = {
    high: 'High confidence',
    medium: 'Moderate confidence',
    low: 'Low confidence',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-opacity hover:opacity-80',
        styles[level]
      )}
    >
      <span className={cn(
        'w-1.5 h-1.5 rounded-full',
        level === 'high' && 'bg-green-500',
        level === 'medium' && 'bg-yellow-500',
        level === 'low' && 'bg-red-500',
      )} />
      {score} — {labels[level]}
    </button>
  );
}

// ── Constants ───────────────────────────────────────────────────────────────

const STATUS_ICONS = {
  success: <Check className="h-3 w-3" />,
  failed: <X className="h-3 w-3" />,
  pending: <Clock className="h-3 w-3 animate-spin" />,
  approval_required: <Lock className="h-3 w-3" />,
};

const STATUS_COLORS = {
  success: 'text-green-600 dark:text-green-400',
  failed: 'text-red-600 dark:text-red-400',
  pending: 'text-blue-600 dark:text-blue-400',
  approval_required: 'text-yellow-600 dark:text-yellow-400',
};

const STATUS_LABELS = {
  success: 'Success',
  failed: 'Failed',
  pending: 'Pending',
  approval_required: 'Awaiting approval',
};

const POLICY_BADGES = {
  auto_approve: { label: 'Auto-approved', icon: '📗', variant: 'secondary' },
  require_approval: { label: 'Approval required', icon: '📙', variant: 'outline' },
  forbidden: { label: 'Forbidden', icon: '📕', variant: 'destructive' },
};

function getToolSource(toolName: string): string {
  if (toolName.startsWith('iq.')) return 'IQ Platform';
  if (toolName.startsWith('ext.')) return 'External Integration';
  if (toolName.startsWith('forge.')) return 'ArgusForge';
  return 'Built-in';
}
